import { useMutation } from "@apollo/client/react";
import { Phone } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { b } from "@/b";
import { AuthErrorCode, getAuthErrorCode } from "../auth-errors";

const START_TOTP_MUTATION = b.mutation("StartTOTP", { phone: "String!" }, (b, v) => [
  b.viewer((b) => [b.startTOTPFlow({ phone: v.phone })]),
]);

export const EnterPhoneNumberStep: React.FC<{
  nextStep: () => void;
}> = ({ nextStep }) => {
  const [phone, setPhone] = useState("");
  // Tracks the last digits we actually submitted so re-edits of the same
  // number don't fire a second startTOTP request.
  const lastSubmittedRef = useRef<string | null>(null);
  const [errorCode, setErrorCode] = useState<AuthErrorCode | null>(null);
  const [startTOTP, { loading, error }] = useMutation(START_TOTP_MUTATION.document(), {
    onCompleted: () => nextStep(),
  });

  const digits = phone.replace(/\D/g, "");

  const submit = (value: string) => {
    if (loading || value.length !== 10) return;
    if (lastSubmittedRef.current === value) return;
    lastSubmittedRef.current = value;
    setErrorCode(null);
    startTOTP({ variables: { phone: value } }).catch((err: unknown) => {
      setErrorCode(getAuthErrorCode(err) ?? null);
      // Allow retry of the same number if the failure was transient/rate-limit.
      // For invalid phone, we keep the lock so the user must edit the number.
      if (getAuthErrorCode(err) !== AuthErrorCode.INVALID_PHONE) {
        lastSubmittedRef.current = null;
      }
    });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: submit and phone are stable per render; we only want to react to digit changes
  useEffect(() => {
    if (digits.length === 10 && !phone.includes("+")) submit(digits);
  }, [digits]);

  const message =
    errorCode === AuthErrorCode.RATE_LIMITED
      ? "Too many requests. Please wait a moment and try again."
      : errorCode === AuthErrorCode.INVALID_PHONE
        ? "That phone number doesn't look right. Please double-check it."
        : error?.message;

  return (
    <div className="text-center space-y-5">
      <div className="flex items-center justify-center size-14 rounded-2xl bg-brand-mint/15 mx-auto">
        <Phone className="size-6 text-gray-700" />
      </div>
      <div>
        <h2 className="text-xl font-bold text-brand-black">Sign In</h2>
        <p className="text-sm text-gray-500 mt-1">Enter your phone number to continue</p>
      </div>
      <input
        type="tel"
        autoComplete="tel"
        placeholder="(555) 555-5555"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        className="w-full px-4 py-3 rounded-xl border border-gray-200 text-center text-lg font-semibold text-brand-black placeholder:text-gray-400 outline-none focus:border-brand-mint focus:ring-2 focus:ring-brand-mint/30 transition-all"
      />
      {message && <p className="text-sm text-red-500">{message}</p>}
      <button
        type="button"
        onClick={() => submit(digits)}
        disabled={loading || digits.length < 10}
        className="w-full py-3 rounded-xl text-sm font-semibold bg-brand-mint text-brand-black hover:bg-brand-mint/90 transition-all cursor-pointer active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {loading ? "Sending..." : "Continue"}
      </button>
    </div>
  );
};
